import React, { Component } from 'react';
import { CONDITIONS } from '../utils/usedGame';
import ProductImagesPickerComponent from './ProductImagesPickerComponent';
import { GAME_CATEGORIES, categoryLabel, isOutOfVocabulary } from '../utils/categories';

var config = process.env;

const GAME_PRODUCT_TYPE = 1;
const PREFILL_LIMIT = 8;
const PREFILL_MIN_CHARS = 3;
const PREFILL_DEBOUNCE_MS = 400;

// El vendedor de un usado es un CLIENTE más: la misma ficha (`user`) que se llena al comprar en
// el mostrador. Por eso la cédula se busca contra `GET /user/:cedula` y, si la ficha existe, los
// datos se traen en vez de pedirlos de nuevo. Los cinco campos son obligatorios en la toma: si
// el cliente es nuevo hay que pedírselos, y si es viejo pero le falta el teléfono, esta es la
// oportunidad de completarlo (el backend completa el hueco sin pisar lo que ya estaba).
const SELLER_FIELDS = ['sellerFirstName', 'sellerLastName', 'sellerPhone', 'sellerEmail'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function digits(value) {
    return String(value ?? '').replace(/\D/g, '');
}

function blank(value) {
    return String(value ?? '').trim() === '';
}

// Form de juego usado. Modo alta (toma): pide vendedor + datos + crédito acordado.
// Modo edición: oculta el vendedor y el crédito (no se cambian) y solo permite
// editar catálogo/condición. Emite los datos por this.props.onChangeData(value).
class FormUsedGameComponent extends Component {
    constructor(props) {
        super(props);
        const initial = props.initial || {};
        this.state = {
            sellerCedula: initial.sellerCedula || '',
            sellerFirstName: initial.sellerFirstName || '',
            sellerLastName: initial.sellerLastName || '',
            sellerPhone: initial.sellerPhone || '',
            sellerEmail: initial.sellerEmail || '',
            // Resultado de la búsqueda de la ficha del cliente: 'idle' | 'loading' | 'found' | 'new'.
            // No es un dato del usado; no viaja en el payload.
            sellerLookup: 'idle',
            name: initial.name || '',
            condition: initial.condition || 'good',
            price: initial.price != null ? initial.price : '',
            agreedCredit: initial.agreedCredit != null ? initial.agreedCredit : '',
            originalPrice: initial.originalPrice != null ? initial.originalPrice : '',
            category: initial.category != null ? initial.category : '',
            minPlayers: initial.minPlayers != null ? initial.minPlayers : '',
            maxPlayers: initial.maxPlayers != null ? initial.maxPlayers : '',
            gameDuration: initial.gameDuration != null ? initial.gameDuration : '',
            barCode: initial.barCode || '',
            sku: initial.sku || '',
            description: initial.description || '',
            internalNotes: initial.internalNotes || '',
            // Precarga desde un producto del catálogo (solo alta). Tampoco viaja en el payload.
            prefillQuery: '',
            prefillResults: [],
            prefillSearching: false,
            prefillFrom: null,
            // No es un dato del usado: es el estado de la carga de imágenes. `submit` lo saca del
            // payload y lo manda aparte como `imagesReady`.
            imagesLoading: props.imagesFolder != null && props.imagesFolder !== '',
            // Cuántas fotos hay cargadas en el picker. Las fotos del usado son OBLIGATORIAS y
            // tienen que ser del ejemplar, así que el form necesita saber si hay alguna.
            imagesCount: 0,
        };
        this._lastLookedUp = null;
        this._prefillTimer = null;
        // Las tres búsquedas de esta pantalla son fetch sin cancelar. Dos respuestas de la misma
        // búsqueda pueden llegar FUERA DE ORDEN, y la que llega última gana aunque sea la vieja.
        // Cada `.then` compara contra estos tokens y descarta lo que ya no corresponde a lo que
        // hay en pantalla. `_mounted` cubre el caso de navegar antes de que vuelva la respuesta.
        this._mounted = true;
        this._prefillQueryEnVuelo = null;
        this._prefillCodeEnVuelo = null;
        // Lo que se cargó de la ÚLTIMA ficha, para poder distinguir "esto lo trajo la búsqueda
        // anterior" de "esto lo tipeó el operador". Ver `limpiarRestosDeFicha`.
        this._fichaAplicada = null;
    }

    componentWillUnmount() {
        clearTimeout(this._prefillTimer);
        this._mounted = false;
    }

    handleChange = (e) => {
        this.setState({ [e.target.name]: e.target.value });
        // El cartel describe la cédula que está EN EL CAMPO. Al empezar a cambiarla vuelve a
        // neutro: dejarlo en "Cliente ya registrado" mientras se tipea otra cédula dice algo
        // que ya no es verdad, y además destraba el "Buscando…" de una búsqueda descartada.
        if (e.target.name === 'sellerCedula') {
            this.setState({ sellerLookup: 'idle' });
        }
    }

    // ---------------------------------------------------------------------------------------
    // Vendedor = cliente
    // ---------------------------------------------------------------------------------------

    authHeaders = () => ({
        'Content-Type': 'application/json',
        'access-token': localStorage.getItem('token'),
    })

    // Una sesión vencida en medio de la toma no se puede tragar: el operador seguiría cargando
    // datos que después no se van a guardar.
    handleExpiredSession = () => {
        window.location.href = localStorage.getItem('homePageURL');
    }

    handleCedulaBlur = () => {
        const cedula = digits(this.state.sellerCedula);
        if (cedula.length < 6 || cedula.length > 9) {
            this.setState({ sellerLookup: 'idle' });
            this._lastLookedUp = null;
            this.limpiarRestosDeFicha();
            return;
        }
        if (cedula === this._lastLookedUp) {
            return;
        }
        this._lastLookedUp = cedula;
        this.limpiarRestosDeFicha();
        this.lookupSeller(cedula);
    }

    // ⚠️ El guard `vigente` no es defensivo de más: sin él, corregir una cédula mal tipeada y
    // salir del campo dos veces seguidas puede terminar con el teléfono y el mail de OTRA
    // persona pegados al vendedor que quedó en pantalla — y ahí es donde se le avisa que su
    // juego se vendió. La respuesta lenta de la cédula vieja gana por llegar última.
    // Al buscar OTRA cédula, los campos que quedaron de la ficha anterior tienen que irse: si no,
    // cambiar una cédula mal tipeada por la de un cliente nuevo deja el nombre, el teléfono y el
    // mail de la persona anterior en pantalla, con el cartel diciendo "Cliente nuevo" — y guardar
    // crea la ficha del cliente nuevo CON LOS DATOS DE OTRO.
    //
    // Sólo se borra lo que sigue igual a como lo dejó la búsqueda anterior. Si el operador lo
    // editó a mano, ese valor es suyo y se respeta (es el caso de "tipeo los datos y después la
    // cédula", que es como se carga un cliente nuevo).
    limpiarRestosDeFicha = () => {
        const previa = this._fichaAplicada;
        if (previa == null) {
            return;
        }
        const limpio = {};
        SELLER_FIELDS.forEach((campo) => {
            if (this.state[campo] === previa[campo]) {
                limpio[campo] = '';
            }
        });
        this._fichaAplicada = null;
        if (Object.keys(limpio).length > 0) {
            this.setState(limpio);
        }
    }

    lookupSeller = (cedula) => {
        const vigente = () => this._mounted && digits(this.state.sellerCedula) === cedula;
        this.setState({ sellerLookup: 'loading' });
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'user/' + encodeURIComponent(cedula), {
            method: 'GET',
            headers: this.authHeaders(),
        })
            .then((results) => results.json())
            .then((data) => {
                if (!vigente()) {
                    return;
                }
                if (data.error > 0) {
                    if (data.error === 1002) {
                        this.handleExpiredSession();
                        return;
                    }
                    // Cualquier otro error: se sigue como si fuera un cliente nuevo. Los cinco
                    // campos son obligatorios igual, así que no se pierde ningún dato — sólo el
                    // ahorro de tipearlos.
                    this.setState({ sellerLookup: 'new' });
                    return;
                }
                const ficha = data.data;
                if (ficha == null) {
                    this.setState({ sellerLookup: 'new' });
                    return;
                }
                // Sólo se pisa lo que la ficha TIENE. Si la ficha no trae el teléfono y el
                // operador ya lo tipeó, se respeta lo tipeado: ese hueco es justamente lo que
                // esta pantalla viene a completar, y pisarlo con vacío lo perdería.
                this.setState((prev) => {
                    const aplicado = {
                        sellerFirstName: blank(ficha.firstName) ? prev.sellerFirstName : ficha.firstName,
                        sellerLastName: blank(ficha.lastName) ? prev.sellerLastName : ficha.lastName,
                        sellerPhone: blank(ficha.phone) ? prev.sellerPhone : ficha.phone,
                        sellerEmail: blank(ficha.email) ? prev.sellerEmail : ficha.email,
                    };
                    this._fichaAplicada = { ...aplicado };
                    return { sellerLookup: 'found', ...aplicado };
                });
            })
            .catch(() => {
                if (vigente()) {
                    this.setState({ sellerLookup: 'new' });
                }
            });
    }

    sellerHint = () => {
        const faltantes = SELLER_FIELDS.filter((f) => blank(this.state[f])).length;
        switch (this.state.sellerLookup) {
            case 'loading':
                return <small className="text-muted">Buscando el cliente…</small>;
            case 'found':
                return faltantes === 0
                    ? <small className="text-success">Cliente ya registrado: se cargaron sus datos.</small>
                    : <small className="text-warning">Cliente ya registrado, pero su ficha está incompleta. Completá lo que falta.</small>;
            case 'new':
                return <small className="text-warning">Cliente nuevo: hay que pedirle todos los datos.</small>;
            default:
                return <small className="text-muted">Escribí la cédula y salí del campo para traer los datos del cliente.</small>;
        }
    }

    // ---------------------------------------------------------------------------------------
    // Precarga desde un producto del catálogo
    // ---------------------------------------------------------------------------------------

    handlePrefillQueryChange = (e) => {
        const prefillQuery = e.target.value;
        this.setState({ prefillQuery });
        clearTimeout(this._prefillTimer);
        if (prefillQuery.trim().length < PREFILL_MIN_CHARS) {
            this.setState({ prefillResults: [], prefillSearching: false });
            return;
        }
        this._prefillTimer = setTimeout(() => this.searchProducts(prefillQuery.trim()), PREFILL_DEBOUNCE_MS);
    }

    // Sólo juegos (productType=1): un accesorio, un plato de la cafetería o un insumo no tienen
    // datos de juego que copiar. El endpoint pagina, así que se piden pocos y se filtra tipeando.
    searchProducts = (query) => {
        // Mismo problema que en la búsqueda del cliente, con consecuencia más leve pero igual de
        // silenciosa: el debounce cancela el TIMER, no un fetch ya en vuelo. Sin este guard, una
        // respuesta lenta de "cat" repuebla la lista después de la de "castle" y el operador
        // copia la ficha de otro juego creyendo que eligió el que buscó.
        this._prefillQueryEnVuelo = query;
        this.setState({ prefillSearching: true });
        const url = config.REACT_APP_BASE_URL + config.REACT_APP_VERSION
            + `product?productType=${GAME_PRODUCT_TYPE}&page=1&limit=${PREFILL_LIMIT}`
            + `&search=${encodeURIComponent(query)}`;
        fetch(url, { method: 'GET', headers: this.authHeaders() })
            .then((results) => results.json())
            .then((data) => {
                if (!this._mounted || this._prefillQueryEnVuelo !== query) {
                    return;
                }
                if (data.error > 0) {
                    if (data.error === 1002) {
                        this.handleExpiredSession();
                        return;
                    }
                    this.setState({ prefillResults: [], prefillSearching: false });
                    return;
                }
                const items = (data.data && data.data.items) || [];
                this.setState({ prefillResults: items, prefillSearching: false });
            })
            .catch(() => {
                if (this._mounted && this._prefillQueryEnVuelo === query) {
                    this.setState({ prefillResults: [], prefillSearching: false });
                }
            });
    }

    // El listado paginado no trae todos los campos de la especialización, así que se pide el
    // detalle del producto elegido. NO se copian: precio de reventa, crédito acordado, SKU,
    // notas internas ni imágenes — todo eso es del ejemplar usado, no del juego del catálogo.
    applyPrefill = (code, name) => {
        // Dos clicks seguidos en "Usar estos datos" de juegos distintos: gana el ÚLTIMO que se
        // pidió, no el que responda último.
        this._prefillCodeEnVuelo = code;
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'product/' + encodeURIComponent(code), {
            method: 'GET',
            headers: this.authHeaders(),
        })
            .then((results) => results.json())
            .then((data) => {
                if (!this._mounted || this._prefillCodeEnVuelo !== code) {
                    return;
                }
                if (data.error > 0) {
                    if (data.error === 1002) {
                        this.handleExpiredSession();
                        return;
                    }
                    alert(data.message);
                    return;
                }
                const producto = (data.data && data.data.product) || {};
                const valor = (v) => (v == null ? '' : v);
                this.setState({
                    name: producto.name || name || '',
                    description: valor(producto.description),
                    category: valor(producto.category),
                    minPlayers: valor(producto.minPlayers),
                    maxPlayers: valor(producto.maxPlayers),
                    gameDuration: valor(producto.gameDuration),
                    barCode: valor(producto.barCode),
                    // El precio del producto nuevo es la referencia de "cuánto sale a estrenar",
                    // que es exactamente lo que significa `originalPrice`. El precio de reventa
                    // NO se toca: lo decide quien recibe el juego mirando el ejemplar.
                    originalPrice: valor(producto.price),
                    prefillFrom: { code, name: producto.name || name },
                    prefillResults: [],
                    prefillQuery: '',
                });
            })
            .catch(() => alert('No se pudieron traer los datos del juego.'));
    }

    clearPrefillOrigin = () => this.setState({ prefillFrom: null })

    prefillBlock() {
        return (
            <div className="card card-small mb-4">
                <div className="card-body p-4">
                    <h6 className="text-uppercase text-muted">Traer datos de un juego del catálogo</h6>
                    <p className="text-muted mb-2">
                        Buscá el juego como producto nuevo y copiá su ficha (nombre, descripción, categoría,
                        jugadores, duración y código de barras) en vez de tipearla de nuevo.
                        <strong> Las fotos no se copian</strong>: tienen que ser del ejemplar usado.
                    </p>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar un juego del catálogo por nombre…"
                        value={this.state.prefillQuery}
                        onChange={this.handlePrefillQueryChange}
                        disabled={this.props.disabled}
                    />
                    {this.state.prefillSearching && (
                        <small className="text-muted d-block mt-2">Buscando…</small>
                    )}
                    {!this.state.prefillSearching
                        && this.state.prefillQuery.trim().length >= PREFILL_MIN_CHARS
                        && this.state.prefillResults.length === 0 && (
                        <small className="text-muted d-block mt-2">
                            No hay juegos del catálogo con ese nombre. Cargá los datos a mano.
                        </small>
                    )}
                    {this.state.prefillResults.length > 0 && (
                        <ul className="list-group mt-2">
                            {this.state.prefillResults.map((item) => (
                                <li key={item.code} className="list-group-item d-flex justify-content-between align-items-center">
                                    <span>{item.name} <span className="text-muted">({item.prefix}{item.code})</span></span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        disabled={this.props.disabled}
                                        onClick={() => this.applyPrefill(item.code, item.name)}
                                    >
                                        Usar estos datos
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {this.state.prefillFrom && (
                        <div className="alert alert-info mt-3 mb-0 d-flex justify-content-between align-items-center">
                            <span>
                                Datos copiados de <strong>{this.state.prefillFrom.name}</strong>. Revisalos y ajustá
                                lo que no coincida con el ejemplar.
                            </span>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.clearPrefillOrigin}>
                                Entendido
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ---------------------------------------------------------------------------------------
    // Imágenes
    // ---------------------------------------------------------------------------------------

    // Las imágenes viajan aparte del resto: no van al backend (que sólo guarda datos del juego),
    // las sube la página al bucket una vez que hay código.
    handleImagesChange = (images) => {
        this.images = images;
        this.setState({ imagesCount: this.countImages(images) });
    }

    // `withThumbnail` agrega una entrada 'thumbnail' que es una COPIA de la primera foto: contar
    // las claves del mapa daría una foto de más. (Ese mismo helper ya descarta las posiciones
    // borradas, así que el filtro por `.path` es sólo un cinturón: no depender de eso desde acá.)
    countImages = (images) => Object.keys(images || {})
        .filter((key) => key !== 'thumbnail' && images[key] && images[key].path)
        .length

    // El picker avisa mientras baja las que el usado YA tiene. Guardar en ese momento borraría
    // del bucket lo que no llegó (la edición vacía la carpeta y reescribe con este mapa), así que
    // el botón queda deshabilitado y la página recibe el flag para no tocar las imágenes.
    handleImagesLoadingChange = (imagesLoading) => { this.setState({ imagesLoading }); }

    // En la toma las fotos son obligatorias; en la edición el aviso es informativo (un usado
    // viejo puede no tener ninguna y aun así hay que poder corregirle el precio).
    imagesWarning() {
        if (!this.props.editMode) {
            return (
                <p className="text-muted">
                    Las fotos son obligatorias y tienen que ser del ejemplar usado: es lo que el comprador
                    mira para saber en qué estado está. No se copian del juego nuevo.
                </p>
            );
        }
        if (this.state.imagesLoading || this.state.imagesCount > 0) {
            return null;
        }
        return (
            <p className="text-warning">
                Este usado no tiene ninguna foto. La publicación muestra el placeholder: cargá al menos
                una del ejemplar.
            </p>
        );
    }

    // ---------------------------------------------------------------------------------------
    // Validación y submit
    // ---------------------------------------------------------------------------------------

    // Devuelve el primer motivo por el que NO se puede guardar, o null. Un solo mensaje y un
    // solo motivo: colapsar varios en un texto genérico deja al operador adivinando.
    validationError = () => {
        const price = Number(this.state.price);

        if (!this.props.editMode) {
            const cedula = digits(this.state.sellerCedula);
            if (cedula.length < 6 || cedula.length > 9) {
                return 'La cédula del vendedor tiene que tener entre 6 y 9 dígitos.';
            }
            if (blank(this.state.sellerFirstName)) return 'Falta el nombre del vendedor.';
            if (blank(this.state.sellerLastName)) return 'Falta el apellido del vendedor.';
            if (blank(this.state.sellerPhone)) return 'Falta el teléfono del vendedor.';
            if (blank(this.state.sellerEmail)) return 'Falta el mail del vendedor.';
            if (!EMAIL_RE.test(String(this.state.sellerEmail).trim())) {
                return 'El mail del vendedor no tiene un formato válido.';
            }
            if (!(Number(this.state.agreedCredit) > 0)) {
                return 'Falta el crédito acordado (tiene que ser mayor que 0).';
            }
        }

        if (blank(this.state.name)) return 'Falta el nombre del juego.';
        if (!(price > 0)) return 'Falta el precio de reventa (tiene que ser mayor que 0).';

        // Las fotos del usado son del ejemplar: sin al menos una, la publicación muestra el
        // placeholder y el comprador no ve en qué estado está el juego que está comprando. Por
        // eso son obligatorias EN LA TOMA, que es el único momento en que el juego está sobre el
        // mostrador y se le puede sacar una foto.
        //
        // En EDICIÓN no bloquea, sólo avisa (ver `imagesWarning`): los usados tomados antes de
        // que el form pidiera fotos tienen cero, y bloquear ahí dejaría sin poder corregirles un
        // precio o una descripción por una foto que quizás ya no se puede sacar (el juego se
        // vendió). Es una regresión sobre una tarea que no tiene nada que ver.
        if (!this.props.editMode && this.state.imagesCount === 0) {
            return 'Cargá al menos una foto del juego usado. Tienen que ser fotos del ejemplar, no del juego nuevo.';
        }

        return null;
    }

    isValid = () => this.validationError() == null

    submit = () => {
        const error = this.validationError();
        if (error != null) {
            alert(error);
            return;
        }
        // Payload explícito: el state tiene además el estado de la búsqueda del cliente, el de la
        // precarga y el de las imágenes, que no son datos del usado y no tienen por qué viajar.
        const datos = {
            sellerCedula: this.state.sellerCedula,
            sellerFirstName: this.state.sellerFirstName,
            sellerLastName: this.state.sellerLastName,
            sellerPhone: this.state.sellerPhone,
            sellerEmail: this.state.sellerEmail,
            name: this.state.name,
            condition: this.state.condition,
            price: this.state.price,
            agreedCredit: this.state.agreedCredit,
            originalPrice: this.state.originalPrice,
            category: this.state.category,
            minPlayers: this.state.minPlayers,
            maxPlayers: this.state.maxPlayers,
            gameDuration: this.state.gameDuration,
            barCode: this.state.barCode,
            sku: this.state.sku,
            description: this.state.description,
            internalNotes: this.state.internalNotes,
        };
        // `imagesReady` es lo que la página mira antes de vaciar la carpeta del bucket.
        this.props.onChangeData({ ...datos, images: this.images || {}, imagesReady: !this.state.imagesLoading });
    }

    field(label, name, type = 'text', extra = {}) {
        return (
            <div className="form-group">
                <label>{label}</label>
                <input
                    type={type}
                    name={name}
                    className="form-control"
                    value={this.state[name]}
                    onChange={this.handleChange}
                    {...extra}
                />
            </div>
        );
    }

    // Antes era un input numérico libre: se podía escribir cualquier número y la
    // tienda lo mostraba como "Sin categoría". Ahora es un select con el vocabulario
    // real. Si la fila que se está editando trae un valor viejo fuera de la lista, se
    // agrega como opción extra: sin eso el select caería a la primera opción y guardar
    // reescribiría la categoría sin que el operador lo pida.
    categoryField() {
        const current = this.state.category;
        return (
            <div className="form-group">
                <label>Categoría</label>
                <select
                    name="category"
                    className="form-control"
                    value={current}
                    onChange={this.handleChange}
                >
                    <option value="">Sin especificar</option>
                    {GAME_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                    {isOutOfVocabulary(current) && (
                        <option value={current}>{categoryLabel(current)}</option>
                    )}
                </select>
            </div>
        );
    }

    render() {
        const editMode = !!this.props.editMode;
        return (
            <>
            {!editMode && (
                <div className="card card-small mb-4">
                    <div className="card-body p-4">
                        <h6 className="text-uppercase text-muted">Vendedor</h6>
                        <p className="text-muted mb-2">
                            El vendedor es un cliente más. Poné la cédula: si ya está registrado se traen sus
                            datos, y si no, hay que pedírselos para dejar la ficha creada.
                        </p>
                        <div className="row">
                            <div className="col-md-4">
                                {this.field('Cédula *', 'sellerCedula', 'text', { onBlur: this.handleCedulaBlur })}
                                {this.sellerHint()}
                            </div>
                            <div className="col-md-4">{this.field('Nombre *', 'sellerFirstName')}</div>
                            <div className="col-md-4">{this.field('Apellido *', 'sellerLastName')}</div>
                        </div>
                        <div className="row">
                            <div className="col-md-4">{this.field('Teléfono *', 'sellerPhone')}</div>
                            <div className="col-md-8">{this.field('Mail *', 'sellerEmail', 'email')}</div>
                        </div>
                    </div>
                </div>
            )}

            {!editMode && this.prefillBlock()}

            <div className="card card-small mb-4">
                <div className="card-body p-4">
                    <h6 className="text-uppercase text-muted">Juego usado</h6>
                    <div className="row">
                        <div className="col-md-6">{this.field('Nombre *', 'name')}</div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Condición</label>
                                <select name="condition" className="form-control" value={this.state.condition} onChange={this.handleChange}>
                                    {CONDITIONS.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3">{this.field('Precio de reventa *', 'price', 'number')}</div>
                    </div>

                    <div className="row">
                        {!editMode && (
                            <div className="col-md-4">{this.field('Crédito acordado *', 'agreedCredit', 'number')}</div>
                        )}
                        <div className="col-md-4">{this.field('Precio original (ref.)', 'originalPrice', 'number')}</div>
                        <div className="col-md-4">{this.field('Código de barras', 'barCode')}</div>
                    </div>

                    <h6 className="text-uppercase text-muted mt-3">Datos del juego (opcional)</h6>
                    <div className="row">
                        <div className="col-md-3">{this.categoryField()}</div>
                        <div className="col-md-3">{this.field('Jugadores mín.', 'minPlayers', 'number')}</div>
                        <div className="col-md-3">{this.field('Jugadores máx.', 'maxPlayers', 'number')}</div>
                        <div className="col-md-3">{this.field('Duración (min)', 'gameDuration', 'number')}</div>
                    </div>

                    <div className="form-group">
                        <label>Descripción</label>
                        <textarea name="description" className="form-control" rows="3" value={this.state.description} onChange={this.handleChange} />
                    </div>
                    <div className="form-group">
                        <label>Notas internas</label>
                        <textarea name="internalNotes" className="form-control" rows="2" value={this.state.internalNotes} onChange={this.handleChange} />
                    </div>

                    <button type="button" className="btn btn-accent" disabled={this.props.disabled || this.state.imagesLoading} onClick={this.submit}>
                        {editMode ? 'Guardar' : 'Registrar usado'}
                    </button>
                    {this.state.imagesLoading && (
                        <span className="text-muted ml-2">Cargando las imágenes del juego…</span>
                    )}
                </div>
            </div>

            {/* Un usado es un producto más para el storefront, así que carga imágenes igual que
                un juego o un accesorio. En edición se le pasa la carpeta para que traiga las que
                ya tiene; en el alta todavía no hay código y por lo tanto no hay carpeta. */}
            <ProductImagesPickerComponent
                storageFolder={this.props.imagesFolder}
                onImagesChange={this.handleImagesChange}
                onLoadingChange={this.handleImagesLoadingChange}
                disabled={this.props.disabled}
                inputId="used-game-image-file"
            />
            {this.imagesWarning()}
            </>
        );
    }
}

export default FormUsedGameComponent;
